import {
  Box,
  Button,
  Text,
  Card,
  HStack,
  Flex,
  Heading,
  chakra,
  Badge,
  Divider,
  GridItem,
  Grid,
  Collapse,
  useDisclosure,
  List,
  ListItem,
} from "@chakra-ui/react";
import DataTableV2 from "components/DataTable/DateTableV2";
import React, { useState, useEffect, useCallback } from "react";
import { isTaskClosed, isTaskRetrying, isTaskRetryExhausted, isThrottleError, taskService } from "api/ai/ai.service";
import { useQuery } from "react-query";
import { formatDate } from "utils";
import { prettyPrintCodeName, toTitleCase } from "utils/text-utils";
import { H2, H2Dashboard } from "components/text/headings";
import userAPI, { getUserName } from "api/user-administration/user-administration";
import { ResponsiveBar } from "@nivo/bar";
import fileinfoService from "api/common/fileinfo.service";
import questionnaireService from "api/issb/questionnaire.service";
import { KB_FOLDER, getFolderForRound } from "api/common/fileinfo";
import { apiGetAISource } from "api/ai/ai.service";
import { DashboardNumber } from "../RequirementsCard";
import { setGlobal } from "context/global";
import { colors } from "theme/foundations/colors";
import { STATUSFIELD_VALUE_LABEL } from "../../../IssbDataInput/components/AdditionalStatuses";
import LocalSpinner from "components/LocalSpinner";
import _ from "lodash";
import { useSelector } from "react-redux";
import PieDialWithTotal from "../PieDialWithTotal";
import DashboardCard from "../DashboardCard";
import { routes } from "config/paths";
import { useNavigate } from "react-router-dom";
import assert from "utils/assert";
import { ResponsiveWaffle } from "@nivo/waffle";
import { H3 } from "components/text/headings";
import { MagicWand, File, FilePdf, FileDoc, FileXls, FileCsv, FilePpt, FileText, GlobeSimple, CaretDown, CaretUp, Sparkle, ArrowUpRight } from "@phosphor-icons/react";
import { getFileType } from "api/common/fileinfo";

import { nivo_theme } from "../chart-utils";
import HighMediumLow from "api/common/HighMediumLow";
import SearchString from "search-string";
import { startCase } from "lodash";
import { useContextualPrompts } from "hooks/query/prompt/useContextualPrompts";
import { useRightSidebar } from "context/RightSidebarContext";
import AIAssistPanel from "../../../components/AIAssistPanel/AIAssistPanel";
import templatePackageService from "api/issb/templatepackage.service";
function StartNewReviewPanel() {
  const navigate = useNavigate();
  const roundSelector = useSelector((state) => state.issbRound);
  const packageId = roundSelector.selectedRound?.packageId;

  const { data: templatePackage } = useQuery({
    queryKey: ["templatePackage", packageId],
    queryFn: () => templatePackageService.get(packageId),
    enabled: !!packageId,
    staleTime: 5 * 60 * 1000,
  });

  const frameworkName = templatePackage?.name || "your framework";

  return (
    <Box
      w="50%"
      border="1px solid"
      borderColor="lime"
      borderRadius="lg"
      bg="lightLime"
      px={6}
      pt={0}
      pb={8}
      position="relative"
      overflow="hidden"
    >
      <chakra.svg
        position="absolute"
        right="0"
        bottom="0"
        width="220px"
        height="180px"
        viewBox="0 0 175 114"
        preserveAspectRatio="xMaxYMax meet"
        fill="none"
        pointerEvents="none"
      >
        <path d="M31.2441 113.272C31.2066 113.31 31.1694 113.349 31.1318 113.387H0C4.3128 108.084 9.24419 102.6 14.8643 96.9404L86.3467 25.458L64.8271 25.4736L64.7959 2.35255H142.156L31.2441 113.272Z" fill="#85C22A" fillOpacity="0.15" />
        <path d="M174.876 18.7002L80.2031 113.387H47.501L158.521 2.36036H174.876V18.7002Z" fill="#85C22A" fillOpacity="0.15" />
        <path d="M174.884 112.425H174.999V113.387H174.87C174.873 113.069 174.876 112.751 174.876 112.433L151.763 112.362V90.6221L128.998 113.387H96.2949L174.884 34.7969V112.425Z" fill="#85C22A" fillOpacity="0.15" />
      </chakra.svg>
      <Heading fontSize="heading.m" fontWeight="700" color="darkTeal" mb={3}>
        New Review
      </Heading>
      <Text fontSize="body.m" color="gray.600" mb={8} position="relative" zIndex={1}>
        Run a review to analyse and generate draft disclosures against the{" "}
        <Text as="span" fontWeight="600">{frameworkName}</Text> requirements.
      </Text>
      <Button
        variant="primary"
        borderRadius="full"
        position="relative"
        zIndex={1}
        onClick={() => navigate(routes.aiReview)}
      >
        Start new review
      </Button>
    </Box>
  );
}

/**
 * Results - by run (aka Task)
 * Figma: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1670-70162&p=f&t=fgPSqli7EHj2tMo9-0
 *
 * See also: AITasksListPanel.jsx
 */
export default function AIDashboard() {
  const [userSelectedTask, setUserSelectedTask] = useState(null);

  const roundSelector = useSelector((state) => state.issbRound);
  const questionnaireId = roundSelector.selectedRound?.id;

  // Get the list of tasks
  const { data: aiTasks, isLoading } = useQuery(
    "taskService.list",
    () => taskService.list({ round_id: questionnaireId }),
    {
      refetchInterval: 5000,
      cacheTime: 0,
      staleTime: 0
    }
  );

  /**
   * if there is running, show that one
   * if there is no running, show the lastest created date
   *
   * @param aiTasks
   */
  const findLatestTask = (aiTasks) => {
    // console.log("findLatestTask", aiTasks);
    if (!aiTasks || aiTasks.length === 0) {
      return null; // Return null if no tasks are available
    }

    // Check for a running task
    const runningTask = aiTasks.find((task) => task.status === "running");
    if (runningTask) {
      return runningTask; // Return the running task if found
    }
    const queuedTask = aiTasks.find((task) => task.status === "queued");
    if (queuedTask) {
      return queuedTask; // Return the queued task if found
    }

    // Find the task with the latest created date
    return aiTasks.sort((a, b) => new Date(b.created_date) - new Date(a.created_date))[0];
  };
  const latestTask = findLatestTask(aiTasks);
  const selectedTask = userSelectedTask || latestTask;

  // get user (disguised async)
  // TODO move this into the user api code as a cache??
  const [user4id, setUser4id] = useState({});
  const promise4id = {};
  const _getUserById = (userId) => {
    if (user4id[userId]) {
      return user4id[userId];
    }
    if (!promise4id[userId]) {
      promise4id[userId] = userAPI.getUserById(userId).then((user) => {
        setUser4id({ ...user4id, [userId]: user });
      });
    }
    return { id: userId }; // return a stub while we wait for the real user
  };
  // skip the sources update tasks
  const _aiTasks = aiTasks?.filter((task) => task.task_type !== "sources_update");
  // add in user name
  const aiTasksWithUser = _aiTasks?.map((task) => ({
    ...task,
    user: _getUserById(task.user_id),
  }));
  // sort by created_date, most recent first
  aiTasksWithUser?.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

  if (isLoading) {
    return <LocalSpinner />;
  }
  // TODO: add in the sources
  const columns = [
    {
      header: "Date",
      accessorFn: (task) => formatDate(task.created_date),
    },
    {
      header: "Run by",
      accessorFn: (task) => getUserName(task.user),
    },
    {
      header: "Requirements",
      accessorFn: (task) => (task.task_type === "question_answer" ? task.inputs?.question_ids?.length : "-"),
    },
    // {
    //   header: "Sources",
    //   accessorFn: (task) => (task.task_type === "sources_update" ? task.inputs?.fileinfo_ids?.length : "-"),
    // },
    {
      header: "Status",
      accessorFn: (task) => toTitleCase(task.status),
    },
    {
      header: "",
      id: "view",
      size: 80,
      cell: ({ row }) => {
        const isSelected = selectedTask?.id === row.original.id;
        return isSelected ? (
          <Badge colorScheme="green" fontSize="xs" px={2} py={0.5} borderRadius="full">
            Viewing
          </Badge>
        ) : (
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "darkTeal" }}
            rightIcon={<ArrowUpRight size={14} />}
            onClick={() => setUserSelectedTask(row.original)}
          >
            View
          </Button>
        );
      },
    },
  ];
  // selected rows (Note: set each render to avoid being cleared by a query refresh)
  if (selectedTask) {
    aiTasksWithUser
      .filter((task) => task.id === selectedTask.id)
      .forEach((task) => {
        task.rowCustomStyles = {
          backgroundColor: "#F0FFF4",
          borderLeft: "3px solid #25C469",
        };
      });
  }

  return (
    <Flex direction="column" gap={4}>
      <StartNewReviewPanel />
      <AITaskDetailsPanel task={selectedTask} aiTasks={aiTasks} isLatest={selectedTask?.id === latestTask?.id} />
      <H2Dashboard>All Reviews</H2Dashboard>
      <DataTableV2
        data={aiTasksWithUser?.slice(0, 10)}
        columns={columns}
        onRowClick={(event, row) => {
          setUserSelectedTask(row.original);
        }}
        queryPageSize={5} // not working, March 2025
        showPaginator={false}
      />
    </Flex>
  );
}

/**
 *
 * @param {*} navigate
 * @param {string[]?} questionIds
 * @param {string?} label
 * @param {URLSearchParams?} searchParams
 */
export function gotoFilteredQuestionList(navigate, questionIds, label = "AI Review", searchParams) {
  assert(
    questionIds === null || Array.isArray(questionIds),
    "questionIds must be an array",
    navigate,
    questionIds,
    label
  );
  const newParams = new URLSearchParams(searchParams);
  newParams.set("rtab", 1); // Navigate to the Requirements tab to show the filtered list
  // HACK: pass the list of question ids to the DataInputPage via a global variable, as it may be too large for a url-param
  if (questionIds) {
    setGlobal("questionIdsFilter", { label, ids: questionIds });
    newParams.set("q", "questionIdsFilter:true");
  }
  navigate(routes.issbRequirements + "?" + newParams.toString());
}

const SOURCE_FILE_TYPE_ICONS = {
  PDF: FilePdf, DOCX: FileDoc, DOC: FileDoc, XLSX: FileXls, XLS: FileXls,
  CSV: FileCsv, PPTX: FilePpt, PPT: FilePpt, TXT: FileText, URL: GlobeSimple,
};

function SourcesAccordion({ sourceInfo, sourceRoundIds, scopePanel, openPanel, setOpenPanel, children }) {
  const isOpen = openPanel === "sources";
  const onToggle = () => setOpenPanel(isOpen ? null : "sources");
  const sources = sourceInfo?.sources || [];

  // Fetch round names for any source rounds
  const { data: sourceRounds } = useQuery(
    ["sourceRoundNames", sourceRoundIds],
    () => Promise.all(sourceRoundIds.map((id) => questionnaireService.get(id))),
    { enabled: !!sourceRoundIds?.length }
  );

  return (
    <Box mb={4}>
      <HStack spacing={3} flexWrap="wrap">
        {children}
        <Badge
          px={2}
          py={1}
          borderRadius="md"
          fontSize="body.xxs"
          colorScheme="gray"
          width="fit-content"
          cursor="pointer"
          onClick={onToggle}
          display="inline-flex"
          alignItems="center"
          gap={1}
        >
          Sources: {sources.length + (sourceRounds?.length || 0) || "..."}
          {isOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </Badge>
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <Box mt={3} p={4} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
          {sources.length === 0 && !sourceRounds?.length ? (
            <Text fontSize="sm" color="gray.500">No sources</Text>
          ) : (
            <Box borderTop="1px solid" borderColor="gray.100">
              {sources.map((source) => {
                const fileType = getFileType(source);
                const IconComponent = SOURCE_FILE_TYPE_ICONS[fileType] || File;
                return (
                  <HStack
                    key={source.id}
                    py={1.5}
                    px={1}
                    spacing={2}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    <Box flexShrink={0}>
                      <IconComponent size={22} color="#718096" />
                    </Box>
                    <Text fontSize="sm" color="darkTeal" noOfLines={1}>{source.filename || source.name || source.id}</Text>
                  </HStack>
                );
              })}
              {sourceRounds?.map((round) => (
                <HStack
                  key={round.id}
                  py={1.5}
                  px={1}
                  spacing={2}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                >
                  <Badge colorScheme="purple" fontSize="xs">Round</Badge>
                  <Text fontSize="sm" color="darkTeal" noOfLines={1}>{round.name || round.id}</Text>
                </HStack>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
      {scopePanel}
    </Box>
  );
}

/**
 * Parse scope filters from the task's stored query filter.
 * Returns { label, conditions[] } where label is "All Requirements" or "(x) Filters".
 */
function getScopeInfo(task) {
  const query = task?.inputs?.filter_label;
  if (!query) return { label: "All Requirements", conditions: [] };

  const search = SearchString.parse(query);
  const conditions = search.getConditionArray();
  if (!conditions.length) return { label: "All Requirements", conditions: [] };

  return {
    label: `(${conditions.length}) Filter${conditions.length !== 1 ? "s" : ""}`,
    conditions: conditions.map((c) => ({
      keyword: startCase(c.keyword),
      value: prettyPrintCodeName(c.value),
    })),
  };
}

/** Renders scope badge + optional collapse panel. Must be used inside SourcesAccordion or similar layout. */
function useScopeAccordion(task, openPanel, setOpenPanel) {
  const isOpen = openPanel === "scope";
  const onToggle = () => setOpenPanel(isOpen ? null : "scope");
  const { label, conditions } = getScopeInfo(task);

  const badge = (
    <Badge
      px={2}
      py={1}
      borderRadius="md"
      fontSize="body.xxs"
      colorScheme="green"
      width="fit-content"
      cursor={conditions.length > 0 ? "pointer" : "default"}
      onClick={conditions.length > 0 ? onToggle : undefined}
      display="inline-flex"
      alignItems="center"
      gap={1}
    >
      Scope: {label}
      {conditions.length > 0 && (isOpen ? <CaretUp size={12} /> : <CaretDown size={12} />)}
    </Badge>
  );

  const panel = conditions.length > 0 ? (
    <Collapse in={isOpen} animateOpacity>
      <Box mt={3} p={4} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
        <List spacing={1}>
          {conditions.map((c, i) => (
            <ListItem key={i} fontSize="sm">
              {c.keyword}: {c.value}
            </ListItem>
          ))}
        </List>
      </Box>
    </Collapse>
  ) : null;

  return { badge, panel };
}

function AITaskDetailsPanel({ task, aiTasks, isLatest }) {
  //   console.log("AITaskDetailsPanel", task);
  const [freshTask, setFreshTask] = useState();
  useEffect(() => {
    setFreshTask(task);
  }, [task]);
  const taskClosed = isTaskClosed(freshTask);
  useQuery({
    queryKey: ["ai-task", task?.id],
    queryFn: () => taskService.get(task.id),
    onSuccess: (data) => {
      setFreshTask(data);
    },
    refetchInterval: taskClosed ? false : 2000,
    enabled: !!task,
  });

  // Fetch user who ran the task
  const { data: taskUser } = useQuery({
    queryKey: ["user", task?.user_id],
    queryFn: () => userAPI.getUserById(task.user_id),
    enabled: !!task?.user_id,
  });

  const roundSelector = useSelector((state) => state.issbRound);
  const packageId = roundSelector.selectedRound?.packageId;
  const { data: templatePackage } = useQuery({
    queryKey: ["templatePackage", packageId],
    queryFn: () => templatePackageService.get(packageId),
    enabled: !!packageId,
    staleTime: 5 * 60 * 1000,
  });

  const [openPanel, setOpenPanel] = useState(null);
  const { badge: scopeBadge, panel: scopePanel } = useScopeAccordion(freshTask, openPanel, setOpenPanel);

  // HACK: this links with the data stashed by the Python backend. It is all a bit adhoc.
  // const inputSources = task?.inputs?.fileinfo_ids?.length;
  const inputQuestions = freshTask?.inputs?.question_ids?.length;
  const outputs = freshTask?.outputs && Object.keys(freshTask?.outputs).length;
  const errorIds = Object.keys(freshTask?.outputs || {}).filter((key) => freshTask?.outputs[key]?.error);
  // Note: outputs includes errors (so we subtract that in the pie chart data below)
  // no-answer if we have an output (ie its not queued) but completeness is unset or none
  let noAnswerIds = Object.keys(freshTask?.outputs || {}).filter(
    (key) =>
      !freshTask?.outputs[key]?.json_content?.completeness ||
      freshTask.outputs[key].json_content.completeness === "NONE"
  );
  noAnswerIds = noAnswerIds.filter((key) => !errorIds.includes(key));
  // answer ids -- the succesful-ish ones
  let answerIds = Object.keys(freshTask?.outputs || {}).filter(
    (key) => freshTask?.outputs[key]?.json_content?.completeness
  );
  answerIds = answerIds.filter((key) => !noAnswerIds.includes(key) && !errorIds.includes(key));

  // Source info
  // HACK: this is from outside the AI Task itself -- because source processing is a sperate task, usually kicked off as they're uploaded.
  const [sourcesFailed, setSourcesFailed] = useState([]);
  // Reset sourcesFailed when switching tasks
  useEffect(() => {
    setSourcesFailed([]);
  }, [task?.id]);
  // Slow, so cache via useQuery
  const { data: sourceInfo, isLoading: isSourceInfoLoading } = useQuery({
    queryKey: ["fetchSourceInfo", task?.id], // Use task?.id (prop) not freshTask?.id to avoid stale renders
    queryFn: () => fetchSourceInfo({ task, aiTasks }),
    onSuccess: (data) => {
      // now check for failed sources (meanwhile we can render the rest)
      fetchSourceInfo_FailedSources(data?.sourcesProcessed).then(setSourcesFailed);
    },
    enabled: !!task,
    refetchInterval: taskClosed ? false : 10000, // refresh while running
    staleTime: 0,
  });
  const analysedCnt = sourceInfo?.sourcesProcessed?.length - (sourcesFailed?.length || 0);
  // get the sources used by this task
  let sourcesUsed;
  if (task?.task_type === "sources_update") {
    // Note: currently these tasks are filtered out
    sourcesUsed = task?.inputs?.fileinfo_ids;
  } else if (freshTask?.outputs) {
    // sources from the outputs?
    // Note: output.json_content.sources is the page-level slice-IDs
    sourcesUsed = Object.values(freshTask.outputs).map((output) => output.json_content?.files);
  }
  sourcesUsed = sourcesUsed && [...new Set(_.flatten(sourcesUsed).filter((x) => x))];
  const sourcesPieData = [
    { id: "analysed", label: "Analysed", value: analysedCnt, color: colors.aiTaskStatus.done },
    {
      id: "unanalysed",
      label: "Not analysed",
      // Note: avoid NaN if any of the values are undefined
      value:
        (sourceInfo?.sources?.length || 0) -
        (analysedCnt || 0) -
        (sourceInfo?.sourcesQueued?.length || 0) -
        (sourcesFailed?.length || 0),
      color: colors.esg_gray[100],
    },
    { id: "queued", label: "Queued", value: sourceInfo?.sourcesQueued?.length, color: colors.esg_gray[200] },
    { id: "error", label: "Failed", value: sourcesFailed?.length, color: colors.aiTaskStatus.failed },
  ];
  const sourcesPieOnClick = datum => {
	console.log(datum);
	console.log("unanalysed", sourceInfo?.unanalysed);
	console.log("sourcesQueued", sourceInfo?.sourcesQueued);
	console.log("sourcesFailed", sourcesFailed);
  };

  //   // completeness - maybe not needed?
  //   const qids4completeness = {};
  //   Object.entries(freshTask?.outputs || {}).forEach(([questionId, output]) => {
  //     const v = output?.json_content?.completeness || HighMediumLow.NONE;
  //     qids4completeness[v] = (qids4completeness[v] || []).concat(questionId);
  //   });
  //   const cnt4completeness = {};
  //   Object.keys(qids4completeness).forEach((key) => {
  //     cnt4completeness[key] = qids4completeness[key]?.length;
  //   });
  // confidence
  const qids4confidence = {};
  Object.entries(freshTask?.outputs || {}).forEach(([questionId, output]) => {
    const v = output?.json_content?.confidence || HighMediumLow.NONE; // Treat confidence:undefined as NONE (see ESG-2987)
    qids4confidence[v] = (qids4confidence[v] || []).concat(questionId);
  });
  const cnt4confidence = {};
  Object.keys(qids4confidence).forEach((key) => {
    cnt4confidence[key] = qids4confidence[key]?.length;
  });

  // Contextual prompts
  const { prompts: dashboardPrompts } = useContextualPrompts({
    location: "ai-review-dashboard",
  });
  const { setRightSidebar, doOpen, activationId } = useRightSidebar();
  const sendToAIChat = useCallback((text) => {
    if (activationId?.startsWith("AIAssist")) {
      window.dispatchEvent(new CustomEvent("ai-chat-send-message", { detail: { text } }));
    } else {
      setRightSidebar(<AIAssistPanel initialInput={text} autoSend />);
      doOpen("AIAssistEdit");
    }
  }, [activationId, setRightSidebar, doOpen]);

  const navigate = useNavigate();

  const pieDialOnClick = (datum) => {
    const qids = {done: answerIds,
		not_done: noAnswerIds,
		error: errorIds
	}[datum.id];
	console.log("pieDialOnClick", datum, qids);
    gotoFilteredQuestionList(navigate, qids, `AI Review: ${datum.id}`);
  };

  // No task, no panel
  if (!freshTask) {
    return null;
  }

  return (
    <>
      <H2Dashboard mt={6}>Review Details</H2Dashboard>
      <Box p={6} border="1px" borderColor="gray.200" borderRadius="lg" bg="white" mb={4} position="relative">
        {isLatest && (
          <Badge position="absolute" top={4} right={4} colorScheme="green" fontSize="xs" px={2} py={0.5} borderRadius="full">
            Latest Review
          </Badge>
        )}
        {/* Header Section */}
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Text color={colors.aiTaskStatus[freshTask?.status]} my={2}>
              {isTaskRetrying(freshTask)
                ? `Retrying (${freshTask.retry_count}/${freshTask.max_retries ?? "?"})`
                : toTitleCase(freshTask?.status)}
            </Text>
            {isTaskRetryExhausted(freshTask) && (
              <Text fontSize="sm" color="red.600" mt={1}>
                {isThrottleError(freshTask?.error)
                  ? `Giving up after ${freshTask.retry_count} retries — Bedrock throttled.`
                  : `Failed after ${freshTask.retry_count} retries.`}
              </Text>
            )}
            <Heading fontSize="lg" color="gray.800" mt={1}>
              AI Review
            </Heading>
            <Text fontSize="sm" color="gray.500">
              {formatDate(freshTask?.created_date, true, false)}
            </Text>
          </Box>
        </Flex>

        {/* Info chips */}
        <SourcesAccordion sourceInfo={sourceInfo} sourceRoundIds={freshTask?.inputs?.sources?.round_ids} scopePanel={scopePanel} openPanel={openPanel} setOpenPanel={setOpenPanel}>
          {taskUser && (
            <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="yellow" width="fit-content">
              Run by: {getUserName(taskUser)}
            </Badge>
          )}
          {templatePackage && (
            <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="green" width="fit-content">
              Framework: {templatePackage.name}
            </Badge>
          )}
          {scopeBadge}
        </SourcesAccordion>

        <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={4} mb={6}>
          {/* Sources */}
          <GridItem overflow="hidden">
            <DashboardCard>
              <H3>Sources Analysed</H3>
              {isSourceInfoLoading ? <LocalSpinner /> : <PieDialWithTotal data={sourcesPieData} completed={analysedCnt} completedLabel="Sources analysed" totalTooltip="Total sources" onClick={sourcesPieOnClick} animate={!taskClosed && (sourceInfo?.sourcesQueued?.length > 0 || sourceInfo?.unanalysed?.length > 0)} />}
            </DashboardCard>
          </GridItem>

          <GridItem overflow="hidden">
            <DashboardCard>
              <H3>Requirements Reviewed</H3>
              <PieDialWithTotal
                data={[
                  { id: "done", label: "Reviewed", value: outputs - errorIds.length, color: colors.aiTaskStatus.done },
                  {
                    id: "not_done",
                    label: "Not done",
                    value: inputQuestions - outputs,
                    color: colors.esg_gray[100],
                  },
                  { id: "error", label: "Failed", value: errorIds.length, color: colors.aiTaskStatus.failed },
                ]}
                completed={outputs - errorIds.length}
                completedLabel="Requirements reviewed"
                totalTooltip="Total requirements"
                onClick={pieDialOnClick}
                animate={!taskClosed && freshTask?.status === "running"}
              />
            </DashboardCard>
          </GridItem>
        </Grid>

        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
          <GridItem>
            <DashboardCard>
              <DashboardNumber
                title="Answers Generated"
                titlePosition="start"
                value={answerIds.length}
                onClick={() => gotoFilteredQuestionList(navigate, answerIds, "AI Review: Answers Generated")}
                cardStyle={true}
              />
            </DashboardCard>
          </GridItem>
          <GridItem>
            <DashboardCard>
              <DashboardNumber
                title="No Answer"
                titlePosition="start"
                value={noAnswerIds.length}
                onClick={() => gotoFilteredQuestionList(navigate, noAnswerIds, "AI Review: No Answer")}
              />
            </DashboardCard>
          </GridItem>
          <GridItem>
            <DashboardCard>
              <DashboardNumber title="Sources Used" titlePosition="start" value={sourcesUsed?.length || 0} />
            </DashboardCard>
          </GridItem>
        </Grid>

        <WaffleChart cnt4confidence={cnt4confidence} qids4confidence={qids4confidence} />

        {/* Contextual prompts */}
        {dashboardPrompts.length > 0 && (
          <>
            <Divider borderColor="gray.200" mt={2} />
            <Box px={5} py={3} opacity={taskClosed ? 1 : 0.4}>
              <HStack spacing={2} mb={3}>
                <Sparkle size={16} weight="fill" color={colors.limeShade[600]} />
                <Text fontSize="body.s" fontWeight="semibold" color="limeShade.600">
                  Analyse
                </Text>
              </HStack>
              <Flex gap={2} flexWrap="wrap">
                {dashboardPrompts.map((p) => (
                  <Button
                    key={p.id}
                    variant="outline"
                    size="sm"
                    borderRadius="full"
                    borderColor="gray.300"
                    color="gray.600"
                    fontWeight="normal"
                    bg="white"
                    isDisabled={!taskClosed}
                    _hover={taskClosed ? { borderColor: "headingLime", color: "darkTeal" } : {}}
                    onClick={() => sendToAIChat(p.content)}
                  >
                    {p.name}
                  </Button>
                ))}
              </Flex>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

function WaffleChart({ cnt4confidence, qids4confidence }) {
  // Format confidence labels
  const formatLabel = (key) => {
    if (key === HighMediumLow.NONE) {
      return "No answer";
    }
    return prettyPrintCodeName(key);
  };

  const data = Object.entries(cnt4confidence).map(([key, value]) => ({
    id: key,
    label: formatLabel(key),
    value: value,
  }));
  const total = Object.values(cnt4confidence).reduce((acc, curr) => acc + curr, 0);
  // const columns = Math.ceil(Math.sqrt(2*total));
  // const rows = Math.ceil(total / columns);
  // const height = Math.max(Math.min(400, rows * 20), 50);

  const columns = total < 40 ? total : 40;
  const rows = Math.ceil(total / columns);

  const height = rows * 30;

  const navigate = useNavigate();

  const handleWaffleClick = (datum) => {
    const confidenceLevel = datum.id;
    const questionIds = qids4confidence[confidenceLevel] || [];
    gotoFilteredQuestionList(navigate, questionIds, `Confidence: ${formatLabel(confidenceLevel)}`);
  };

  // Note: ResponsiveWaffle is actually more work and more fragile than just rendering a grid of squares. But maybe there are little benefits built into nivo.

  return (
    // Note: mb={6} creates a bit of necc space that the waffle doesn't consume
    <Flex direction="column" width="100%">
      <H3>Confidence</H3>
      <Flex justifyContent="flex-start" height={`${height + 70}px`}>
        <ResponsiveWaffle
          data={data}
          total={total}
          rows={rows}
          columns={columns}
          padding={1}
          valueFormat=".1f"
          pixelRatio={1}
          margin={{ top: 50 /* space for the legend */ }}
          colors={(d) => colors.highMediumLow[d.id]}
          borderRadius={3}
          borderColor={{
            from: "color",
            modifiers: [["darker", 0.3]],
          }}
          motionStagger={2}
          onClick={handleWaffleClick}
          legends={[
            {
              anchor: "top",
              direction: "row",
              justify: false,
              translateX: 0,
              translateY: -30,
              itemsSpacing: 20,
              itemWidth: 120,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 1,
              itemTextColor: "#777",
              symbolSize: 20,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemTextColor: "#000",
                    itemBackground: "#f7fafb",
                  },
                },
              ],
              format: (value) => formatLabel(value),
            },
          ]}
          theme={nivo_theme}
        />
      </Flex>
    </Flex>
  );
}

/**
 * HACK put together sources info from a few places. Can be slow
 * @returns {
 *   sources: FileInfo[] from fineinfoService.list,
 *   sourcesProcessed: FileInfo[] subset of sources with aiDate,
 *   sourcesQueued: FileInfo[] subset of sources ,
 *   unanalysed: FileInfo[] subset of sources ,
 * }
 */
async function fetchSourceInfo({ task, aiTasks }) {
  const inputSources = task?.inputs?.sources || {};
  const prefer_fileinfo_ids = inputSources.prefer_fileinfo_ids || [];
  const other_fileinfo_ids = inputSources.other_fileinfo_ids || [];
  const exclude_fileinfo_ids = inputSources.exclude_fileinfo_ids || [];
  let fileinfoIds = [...prefer_fileinfo_ids, ...other_fileinfo_ids].filter((id) => !exclude_fileinfo_ids.includes(id));
  fileinfoIds = [...new Set(fileinfoIds)];

  let sources = [];
  if (fileinfoIds.length) {
    const fileinfosPage = await fileinfoService.list({
      ids: fileinfoIds,
    });
    sources = fileinfosPage.content;
  } else if (task?.inputs?.round_id && isTaskClosed(task) && inputSources.other_fileinfo_ids == null) {
    // Fallback for historic tasks where other_fileinfo_ids was not stored at all.
    // Does NOT apply when other_fileinfo_ids is [] (user explicitly deselected all files).
    const folder = getFolderForRound(task.inputs.round_id);
    const fileinfosPage = await fileinfoService.list({ query: "folder:" + folder });
    sources = fileinfosPage?.content || [];
  }
  // filter out KB
  sources = sources.filter((f) => f.folders[0] !== KB_FOLDER);

  let sourcesProcessed = sources.filter((f) => f.aiDate);
  // fails? - do separately to speed up rendering
  // What is in the queue? TODO test this!
  let sourcesQueued = [];
  aiTasks
    ?.filter((t) => t.task_type === "sources_update" && !isTaskClosed(t))
    .forEach((t) => {
      let queuedFileinfoIds = Object.keys(t.inputs?.fileinfo_ids || {});
      sourcesQueued = sourcesQueued.concat(queuedFileinfoIds);
    });
  sourcesQueued = [...new Set(sourcesQueued)].filter((id) => !sourcesProcessed.find((f) => f.id === id));
  let unanalysed = sources.filter(fi => ! sourcesProcessed.includes(fi) && ! sourcesQueued.includes(fi));
  // Note: sourcesUsed can be done faster, so it is done separately
  const sourceInfo = {
    sources,
    sourcesProcessed,
    sourcesQueued,
	unanalysed
  };
  return sourceInfo;
} // ./fetchSourceInfo

// Note: split out from fetchSourceInfo to speed up rendering
async function fetchSourceInfo_FailedSources(sources) {
  if (!sources) return null;
  const promises = sources?.map((f) => apiGetAISource(f));
  const sourcesFailed = (await Promise.all(promises)).filter((f) => {
    return f?.error;
  });
  return sourcesFailed;
}

/**
 * keys e.g. ["NONE", "LOW", "MEDIUM", "HIGH"]
 */
function BarChart({ statusField, keys, cnt4key, onClick }) {
  if (!keys) keys = Object.keys(cnt4key);
  const data = keys.map((key) => ({
    k: key,
    value: cnt4key[key] || 0,
    color: colors.highMediumLow[key],
  }));
  const labeller = (d) => {
    const label4value = STATUSFIELD_VALUE_LABEL[statusField] || {};
    return label4value[d] || d;
  };

  return (
    <Box width="100%" height="350px">
      <ResponsiveBar
        data={data}
        keys={["value"]}
        indexBy="k"
        margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
        padding={0.3}
        valueScale={{ type: "linear" }}
        enableGridX={false}
        enableGridY={false}
        axisLeft={null}
        axisBottom={{
          format: (d) => labeller(d),
          tickRotation: -15,
        }}
        // label={labeller}
        labelSkipWidth={0}
        labelSkipHeight={1}
        colors={(d) => d.data.color}
        onClick={(click) => onClick && onClick(click.indexValue, click)}
      />
    </Box>
  );
}
